import CharacterCount from '@/components/form/CharacterCount'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import { useAppForm } from '@/lib/form'
import { ORDER_FIELD_MAX_LENGTHS, ORDER_FIELD_NATIVE_MAX_LENGTHS } from '@/lib/order-field-limits'
import { cartHasSubscriptionPackageItems } from '@/lib/pos/subscriptions'
import { useIsCartMutating, useUpdateCartMutation } from '@/lib/services/carts/carts.query'
import { getErrorMessage } from '@/lib/utils'
import { MessageCircleHeart, Pencil, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

function getPersonalizationFormDefaults(cart) {
  return {
    card_message: cart?.card_message ?? '',
    signature: cart?.signature ?? '',
  }
}

function PersonalizationModal({
  open,
  onOpenChange,
  cart,
  createCartWithDefaults,
  disabled = false,
}) {
  const updateCartMutation = useUpdateCartMutation()
  const isCartMutating = useIsCartMutating()

  const form = useAppForm({
    defaultValues: getPersonalizationFormDefaults(cart),
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        if (cartHasSubscriptionPackageItems(cart)) {
          const hasCardMessage = Boolean(value.card_message.trim())
          const hasSignature = Boolean(value.signature.trim())

          if (hasCardMessage !== hasSignature) {
            return {
              fields: hasCardMessage
                ? {
                    signature: [
                      'Enter a signature or clear the card message for this subscription.',
                    ],
                  }
                : {
                    card_message: [
                      'Enter a card message or clear the signature for this subscription.',
                    ],
                  },
            }
          }
        }

        try {
          let currentCartId = cart?.id

          if (!currentCartId) {
            const newCart = await createCartWithDefaults?.()
            currentCartId = newCart?.id
          }

          if (!currentCartId) {
            throw new Error('Unable to create a POS cart.')
          }

          await updateCartMutation.mutateAsync({
            cartId: currentCartId,
            data: {
              card_message: value.card_message,
              signature: value.signature,
            },
          })

          toast.success('Card message and signature updated successfully')
          formApi.reset()
          onOpenChange(false)
        } catch (error) {
          toast.error('Failed to update card message and signature', {
            description: getErrorMessage(error) || 'Please try again.',
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  const isPending = disabled || updateCartMutation.isPending || isCartMutating > 0

  const handleClose = () => {
    form.reset(getPersonalizationFormDefaults(cart))
    onOpenChange(false)
  }

  const handleDialogOpenChange = (isOpen) => {
    if (!isOpen) {
      handleClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Card Message & Signature</DialogTitle>
          <DialogDescription>
            Add or update the enclosure card message for this order.
          </DialogDescription>
        </DialogHeader>

        <form.AppForm>
          <form.Form>
            <div className="space-y-4 pt-4">
              <form.AppField name="card_message">
                {(field) => {
                  return (
                    <div className="space-y-1">
                      <field.TextareaInput
                        id="pos-card-message"
                        label="Card Message"
                        placeholder="Card message"
                        maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.card_message}
                        characterMaxLength={ORDER_FIELD_MAX_LENGTHS.card_message}
                        rows={4}
                      />
                      <CharacterCount
                        value={field.state.value}
                        maxLength={ORDER_FIELD_MAX_LENGTHS.card_message}
                      />
                    </div>
                  )
                }}
              </form.AppField>

              <form.AppField name="signature">
                {(field) => (
                  <div className="space-y-1">
                    <field.TextInput
                      id="pos-card-signature"
                      label="Signature"
                      maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.signature}
                      characterMaxLength={ORDER_FIELD_MAX_LENGTHS.signature}
                      placeholder="Signature"
                    />
                    <CharacterCount
                      value={field.state.value}
                      maxLength={ORDER_FIELD_MAX_LENGTHS.signature}
                    />
                  </div>
                )}
              </form.AppField>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" type="button" onClick={handleClose} disabled={isPending}>
                Cancel
              </Button>
              <form.SubmitButton className="w-auto" mutation={{ isPending }}>
                Save
              </form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  )
}

export function PersonalizationSection({ cart, createCartWithDefaults, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false)
  const updateCartMutation = useUpdateCartMutation()
  const isCartMutating = useIsCartMutating()
  const hasCardMessage = Boolean(cart?.card_message)
  const hasSignature = Boolean(cart?.signature)
  const hasPersonalization = hasCardMessage || hasSignature
  const isDisabled = disabled || isCartMutating > 0 || updateCartMutation.isPending

  const clearPersonalization = async () => {
    if (!cart?.id) return

    try {
      await updateCartMutation.mutateAsync({
        cartId: cart.id,
        data: {
          card_message: null,
          signature: null,
        },
      })
      toast.success('Card message and signature cleared')
    } catch (error) {
      toast.error('Failed to clear card message and signature', {
        description: getErrorMessage(error) || 'Please try again.',
      })
    }
  }

  return (
    <div className="mb-4 space-y-2">
      <Typography variant="label" tone="muted">
        Personal touches
      </Typography>

      <div className="rounded-lg bg-muted/50 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="rounded-md border border-border bg-card p-2">
              <MessageCircleHeart className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              {hasPersonalization ? (
                <>
                  {hasCardMessage ? (
                    <Typography variant="labelStrong" className="whitespace-pre-wrap break-words">
                      {cart.card_message}
                    </Typography>
                  ) : (
                    <Typography variant="labelStrong">Card Message & Signature</Typography>
                  )}
                  {hasSignature ? (
                    <Typography variant="label" tone="muted" className="mt-2 block">
                      — {cart.signature}
                    </Typography>
                  ) : null}
                </>
              ) : (
                <>
                  <Typography variant="labelStrong">Card Message & Signature</Typography>
                  <Typography variant="label" tone="muted" className="mt-1 block">
                    No card message added yet.
                  </Typography>
                </>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => setIsOpen(true)}
            disabled={isDisabled}
            aria-label={
              hasPersonalization ? 'Edit Card Message & Signature' : 'Add Card Message & Signature'
            }
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {hasPersonalization ? (
            <Button
              variant="ghostDestructive"
              size="icon"
              className="shrink-0"
              onClick={clearPersonalization}
              disabled={isDisabled}
              aria-label="Clear Card Message & Signature"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      {isOpen ? (
        <PersonalizationModal
          key={`${cart?.id ?? 'new'}:${cart?.card_message ?? ''}:${cart?.signature ?? ''}`}
          open={isOpen}
          onOpenChange={setIsOpen}
          cart={cart}
          createCartWithDefaults={createCartWithDefaults}
          disabled={isDisabled}
        />
      ) : null}
    </div>
  )
}
